/**
 * Authentication types for the GDB Cloud Edition.
 *
 * These types match the Rust server's JSON response shapes from
 * /api/auth/* endpoints (see server/src/server/handlers/platform/auth.rs).
 */

/**
 * Deployment environment the backend is running in, reported on the
 * auth-status response. Drives env-dependent panel visibility (alpha modules
 * are visible in development/staging but hidden in production —
 * see `isVisibleInPanel`).
 *
 * `as const`-style union (no enum) per typescript-standards.
 */
export const GDB_ENVIRONMENTS = [
  'development',
  'staging',
  'production',
] as const
export type GdbEnvironment = (typeof GDB_ENVIRONMENTS)[number]

/**
 * Default environment when the backend hasn't reported one yet (status still
 * loading, or an older sidecar that predates the field). Defaults to the
 * MOST RESTRICTIVE environment so pre-release (alpha) modules never leak
 * before the real environment resolves. Centralized here so every caller
 * (`useAuth`, `isVisibleInPanel`) agrees on the fallback.
 */
export const DEFAULT_ENVIRONMENT: GdbEnvironment = 'production'

/** User account information returned after login/register/refresh. */
export interface AuthUser {
  id: string
  email: string
  name: string | null
  tier: string
  role: string
  /**
   * Phase 3 (CON-01): Super-admin claim. Gates `/console/*` routes via
   * <RequireSuperAdmin> and `/api/cms/admin/*` write endpoints via the
   * SuperAdminUser axum extractor.
   *
   * Optional for backwards-compat with cached sessions issued before
   * Phase 3 ships — the `default` attribute on the Rust `HubClaims` field
   * means pre-Phase-3 JWTs decode with `is_super_admin = false`, and the
   * /api/auth/status response can omit the field entirely on those
   * sessions. Treated as `false` when missing.
   *
   * Distinct from `role: 'admin' | 'user'`: `role` gates the LEGACY
   * `/admin/*` surface (retired in Phase 12); `isSuperAdmin` gates the
   * NEW `/console/*` surface introduced in Phase 3. They coexist.
   */
  isSuperAdmin?: boolean
  /**
   * Phase 19 Plan 19-02: multi-role grants from the `user_roles` table.
   *
   * Always emitted as an array — empty (`[]`) when the user has no
   * grants, never null/missing. Allowed values are constrained at the
   * database layer (CHECK constraint) to:
   *   - `alpha_tester` — internal staff + closest-circle testers
   *   - `beta_tester`  — broader opt-in beta cohort
   *   - `staff`        — GDB team members (separate from super-admin)
   *
   * Distinct from `role` (singular, legacy `/admin/*` gate) and
   * `isSuperAdmin` (super-admin gate). The dashboard (Plan 19-05)
   * derives `isTester` from this array to gate pre-release modules
   * (`releasePhase` in `'planning' | 'alpha' | 'beta'`).
   *
   * The Rust `UserInfo.roles` field is `Vec<String>` (non-Optional), so
   * the wire shape is always present after a fresh refresh/login/me.
   * Pre-Phase-19 JWT cookies decode without the field at the JWT layer,
   * but `do_refresh` always re-queries the DB and returns `[]` for
   * users without grants — so the cookie age doesn't change the
   * `/api/auth/me` payload.
   */
  roles: string[]
  /**
   * Backend-computed tester flag (added in V2 production after V3's May 14
   * fork). Set true when the user is in the tester cohort according to backend
   * rules (role='admin', beta_enrolled, or any grant in user_roles). The
   * dashboard short-circuits its per-phase gating on this flag so it doesn't
   * have to reproduce the backend's fan-in logic. Optional for back-compat
   * with older Rust UserInfo emissions.
   */
  isTester?: boolean
  /**
   * V2 sync (Theme 10, Phase 19-14) — server-derived as
   * `isSuperAdmin || isTester`. The SPA gates its `window.onerror` / React
   * error boundary forwarding on this flag (defense in depth — the server's
   * `/api/telemetry/event` handler also re-checks). Optional for
   * backwards-compat; treated as `false` when missing so pre-Theme-10
   * sessions don't accidentally leak telemetry.
   */
  telemetryEnabled?: boolean
}

/** Module license access status for a single module. */
export interface ModuleAccess {
  id: string
  name: string
  hasAccess: boolean
  canUpdate: boolean
  hasSourceAccess?: boolean
}

/**
 * Full authentication status returned by login, register, and refresh endpoints.
 * Maps to the Rust LoginData struct.
 */
export interface AuthStatus {
  loggedIn: boolean
  valid: boolean
  token?: string
  user: AuthUser | null
  modules: ModuleAccess[]
  /**
   * Deployment environment the backend is running in
   * (`development | staging | production`). Drives env-dependent panel
   * visibility (alpha modules hide in production — see `isVisibleInPanel`).
   *
   * Optional for backwards-compat with older sidecars / cached statuses that
   * predate the field; consumers (`useAuth`) default it to
   * `DEFAULT_ENVIRONMENT` (the most restrictive) when absent.
   */
  environment?: GdbEnvironment
  betaEnrolled?: boolean
  maintenanceActive: boolean
  maintenanceExpiresAt?: string | null
  needsRefresh: boolean
  offlineGraceDays: number
  error: string | null
}

/** Standard API response wrapper used by all auth endpoints. */
export interface AuthResponse<T> {
  success: boolean
  data?: T
  error?: string
}

/** Response from the forgot-password endpoint. */
export interface ForgotPasswordResult {
  success: boolean
  message: string | null
  error: string | null
}

/**
 * Result of GET /api/auth/validate-reset-token?token=xxx.
 *
 * The backend returns `200 { valid: true }` for a live token or
 * `400 { valid: false, error }` for a missing/expired one. Wrapped in
 * `AuthResponse` by `validateResetToken`, so a non-200 degrades to
 * `{ success: false, error }` — the caller treats either signal (HTTP
 * non-ok OR `valid: false`) as "this link is dead".
 */
export interface ValidateResetTokenResult {
  valid: boolean
  error?: string | null
}

/**
 * Result of POST /api/auth/reset-password.
 *
 * On success the backend consumes the token and sets the user's password,
 * returning a success JSON; on failure it returns `400` with an `error`
 * ("Invalid or expired reset token" / "Password must be 8-256 characters").
 * `resetPassword` wraps it in `AuthResponse` so the form reads
 * `success`/`error` the same way `forgotPassword` does.
 */
export interface ResetPasswordResult {
  success: boolean
  message?: string | null
  error?: string | null
}
